import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { path: "/dashboard", icon: "⬡", label: "Dashboard" },
  { path: "/history", icon: "◷", label: "Stake History" },
  { path: "/audit", icon: "⊞", label: "Audit Tool", adminOnly: true },
  { path: "/amortization", icon: "⊟", label: "Loan Schedule" },
  { path: "/admin", icon: "⬙", label: "Admin Panel", adminOnly: true },
];

export default function Layout({ children }) {
  const { currentUser, userRole, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const visibleItems = navItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <div style={styles.root}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, width: collapsed ? "68px" : "220px" }}>
        {/* Logo */}
        <div style={styles.logoArea}>
          <div style={styles.logoMark}>◈</div>
          {!collapsed && (
            <div>
              <div style={styles.logoText}>FTPS</div>
              <div style={styles.logoSub}>Stake Tracker</div>
            </div>
          )}
          <button
            style={styles.collapseBtn}
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {visibleItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
                justifyContent: collapsed ? "center" : "flex-start",
              })}
              title={collapsed ? item.label : ""}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User area */}
        <div style={styles.userArea}>
          {!collapsed && (
            <div style={styles.userInfo}>
              <img
                src={currentUser?.photoURL || `https://ui-avatars.com/api/?name=${currentUser?.displayName}&background=6c63ff&color=fff`}
                alt="avatar"
                style={styles.avatar}
              />
              <div style={styles.userDetails}>
                <div style={styles.userName}>{currentUser?.displayName?.split(" ")[0]}</div>
                <div style={styles.userRole}>
                  <span className={`badge badge-${userRole}`}>{userRole}</span>
                </div>
              </div>
            </div>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn} title="Sign out">
            <span style={{ fontSize: "16px" }}>⇥</span>
            {!collapsed && <span style={{ fontSize: "13px" }}>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
}

const styles = {
  root: {
    display: "flex",
    minHeight: "100vh",
    background: "var(--bg-primary)",
  },
  sidebar: {
    background: "var(--bg-secondary)",
    borderRight: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column",
    transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
    overflow: "hidden",
    flexShrink: 0,
    position: "sticky",
    top: 0,
    height: "100vh",
  },
  logoArea: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "20px 16px",
    borderBottom: "1px solid var(--border)",
  },
  logoMark: {
    width: "36px",
    height: "36px",
    background: "var(--accent-dim)",
    border: "1px solid var(--border-accent)",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    color: "var(--accent-bright)",
    flexShrink: 0,
  },
  logoText: {
    fontFamily: "var(--font-display)",
    fontWeight: 800,
    fontSize: "16px",
    letterSpacing: "0.08em",
    color: "var(--text-primary)",
  },
  logoSub: {
    fontSize: "10px",
    color: "var(--text-muted)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  collapseBtn: {
    marginLeft: "auto",
    background: "none",
    border: "none",
    color: "var(--text-muted)",
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "4px",
    flexShrink: 0,
    lineHeight: 1,
  },
  nav: {
    flex: 1,
    padding: "12px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    overflowY: "auto",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 10px",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-secondary)",
    fontSize: "14px",
    fontWeight: 500,
    transition: "var(--transition)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  navItemActive: {
    background: "var(--accent-dim)",
    color: "var(--accent-bright)",
    borderLeft: "2px solid var(--accent)",
  },
  navIcon: {
    fontSize: "18px",
    flexShrink: 0,
    width: "20px",
    textAlign: "center",
  },
  navLabel: {
    fontSize: "14px",
  },
  userArea: {
    padding: "12px 8px",
    borderTop: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px",
    borderRadius: "var(--radius-sm)",
    background: "var(--bg-card)",
  },
  avatar: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  userDetails: {
    overflow: "hidden",
    minWidth: 0,
  },
  userName: {
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--text-primary)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userRole: {
    marginTop: "2px",
  },
  logoutBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "8px",
    borderRadius: "var(--radius-sm)",
    background: "none",
    color: "var(--text-muted)",
    border: "none",
    cursor: "pointer",
    width: "100%",
    transition: "var(--transition)",
    fontSize: "14px",
  },
  main: {
    flex: 1,
    padding: "32px",
    overflowY: "auto",
    minWidth: 0,
  },
};
